import { getTokenImageUrl } from '@/domain/euler'
import { usePointsData } from '@/domain/points/pointsData/usePointsData'
import { assets } from '@/ui/assets'
import { TokenImage } from '@/ui/atoms/token-image/TokenImage'
import { Tooltip, TooltipContentLong, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { getPointsVisibility } from '@/ui/organisms/market-entry/utils/getPointsVisibility'

import { resolvePointsDisplay } from '@/ui/organisms/market-entry/utils/resolvePointsDisplay'
import { ArrowRight, Sparkles } from 'lucide-react'
import Image from 'next/image'
import { getLendingOnlyAPY } from '../logic/useUnifiedEarnFilteredOpportunities'
import type { YieldOpportunity } from '../types'
import { getMarketType, getMarketTypeDescription, getPointsType, getYieldOpportunityDetailsPath } from '../utils'
import { SectionHeaderTitle } from '@/ui/molecules/section-header/SectionHeader'

function formatTVL(tvl: number | undefined): string {
  if (!tvl) return '$0'
  if (tvl >= 1_000_000_000) return `$${(tvl / 1_000_000_000).toFixed(2)}B`
  if (tvl >= 1_000_000) return `$${(tvl / 1_000_000).toFixed(2)}M`
  if (tvl >= 1_000) return `$${(tvl / 1_000).toFixed(2)}K`
  return `$${tvl.toFixed(2)}`
}

interface TopEarningSectionProps {
  opportunities: YieldOpportunity[]
  onOpportunityClick: (opp: YieldOpportunity) => void
}

export function TopEarningSection({ opportunities, onOpportunityClick }: TopEarningSectionProps) {
  if (opportunities.length === 0) {
    return null
  }

  return (
    <div className="flex flex-col gap-3 py-2">
      <SectionHeaderTitle icon={<Sparkles className="h-4 w-4 text-[#FBE572]" />} title="Featured Opportunities" />
      <div className="-mx-3 sm:mx-0">
        <div className="no-scrollbar flex gap-3 overflow-x-auto px-3 py-1 sm:grid lg:grid-cols-4 sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:px-0 sm:py-1">
          {opportunities.map((opp, i) => (
            <div
              key={`${opp.id}-${getYieldOpportunityDetailsPath(opp)}-${opp.market?.key ?? opp.sourceData.type}`}
              className="w-[280px] shrink-0 sm:w-full"
              style={{
                animationDelay: `${i * 0.08}s`,
                animationName: 'sweep-card-in',
                animationDuration: '0.4s',
                animationTimingFunction: 'ease-out',
                animationFillMode: 'forwards',
                opacity: 0,
              }}
            >
              <TopEarningCard opportunity={opp} onClick={() => onOpportunityClick(opp)} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

interface TopEarningCardProps {
  opportunity: YieldOpportunity
  onClick: () => void
}

function TopEarningCard({ opportunity, onClick }: TopEarningCardProps) {
  const tokenImage = getTokenImageUrl(opportunity.tokenInfo, opportunity.assetSymbol)
  const marketTypeLabel = getMarketType(opportunity)

  const { getByAddress } = usePointsData()
  const pointsType = getPointsType(opportunity.source)
  const points = getByAddress({
    type: pointsType,
    action: 'deposit',
    address: opportunity.address,
  })

  const lendContextPoints = opportunity.partnerPoints?.filter((p) => p.lend || p.collateral)
  const { hasHypurrPoints, hasPartnerPoints, isEarnVault } = resolvePointsDisplay({
    entryPartnerPoints: lendContextPoints,
    pointsInfo: { multiplier: points?.multiplier },
    isEarnVault: opportunity.sourceData.type === 'evault',
  })
  const { hasVisiblePoints: showPointsBadge } = getPointsVisibility({
    hasHypurrPoints,
    hasPartnerPoints,
    isEarnVault,
  })

  const lendingAPY = getLendingOnlyAPY(opportunity)

  return (
    <div
      onClick={onClick}
      className="group relative cursor-pointer rounded-xl bg-gradient-to-r from-gradient-yellow/20 to-gradient-green/20 p-[1px] transition-all sm:rounded-2xl hover:from-gradient-yellow/40 hover:to-gradient-green/40"
    >
      <div className="flex h-full min-h-[120px] flex-col justify-between overflow-hidden rounded-xl bg-card p-3 sm:min-h-[140px] sm:rounded-2xl sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-start gap-2 sm:gap-3">
            <TokenImage
              src={tokenImage}
              alt={opportunity.assetSymbol}
              width={24}
              height={24}
              className="h-6 w-6 shrink-0 rounded-full sm:h-7 sm:w-7"
              fallbackSrc={opportunity.tokenInfo?.logoURI}
            />
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-semibold text-base text-white sm:text-lg">{opportunity.assetSymbol}</span>
              <Tooltip>
                <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <span className="max-w-[100px] cursor-help truncate text-muted-foreground text-xs sm:max-w-[120px] hover:text-foreground">
                    {marketTypeLabel}
                  </span>
                </TooltipTrigger>
                <TooltipContentLong>
                  <div className="max-w-xs space-y-1 text-sm">
                    <p className="font-medium">{marketTypeLabel}</p>
                    <p className="text-muted-foreground">{getMarketTypeDescription(opportunity)}</p>
                  </div>
                </TooltipContentLong>
              </Tooltip>
              <span className="mt-1 text-[11px] text-muted-foreground/80">
                {formatTVL(Number(opportunity.tvlUsd))} TVL
              </span>
            </div>
          </div>

          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform sm:h-10 sm:w-10 group-hover:scale-110">
            <ArrowRight className="h-4 w-4 text-black sm:h-5 sm:w-5" />
          </div>
        </div>

        <div className="mt-3 flex items-end sm:mt-4">
          <div className="flex items-baseline gap-1">
            <Sparkles className="mb-0.5 h-3 w-3 text-gradient-yellow sm:mb-1 sm:h-4 sm:w-4" />
            <span className="bg-gradient-to-r from-gradient-yellow to-gradient-green bg-clip-text font-bold text-2xl text-transparent sm:text-4xl">
              {lendingAPY > 0 ? `${lendingAPY.toFixed(2)}%` : '0.00%'}
            </span>
            <span className="ml-0.5 font-medium text-muted-foreground text-xs sm:ml-1 sm:text-sm">APY</span>
            {showPointsBadge && (
              <span className="flex items-center gap-1 rounded border border-white/20 px-1 py-1">
                <Image src={assets.hypurrPawNoShadow} alt="HypurrFi Points" className="h-3.5 w-3.5" />
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
